import { useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { performPasswordReset } from "../features/auth/api";

export function ResetPasswordPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const isDisabled = useMemo(
    () => submitting || newPassword.length < 8 || mismatch || !token,
    [mismatch, newPassword.length, submitting, token],
  );

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await performPasswordReset(token, newPassword);
      setDone(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Couldn't reset your password.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="app-shell auth-shell">
      <section className="hero fade-in">
        <p className="eyebrow">
          <span className="brand-wordmark">
            0todeploy <span className="brand-vms">VMS</span>
          </span>
        </p>
        <h1>Choose a new password</h1>
      </section>

      <section className="panel auth-card fade-in delay-1">
        <header className="panel-header">
          <h2>Reset Password</h2>
        </header>
        {!token ? <p className="error-banner">This reset link is missing its token.</p> : null}
        {done ? (
          <>
            <p className="success-banner">Password updated. You can now sign in.</p>
            <p className="auth-switch">
              <Link to="/login">Go to sign in</Link>
            </p>
          </>
        ) : token ? (
          <form className="auth-form" onSubmit={onSubmit}>
            <label htmlFor="reset-new-password">New Password</label>
            <input
              id="reset-new-password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder="At least 8 characters"
            />

            <label htmlFor="reset-confirm-password">Confirm Password</label>
            <input
              id="reset-confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Re-enter the new password"
            />
            {mismatch ? <span className="field-hint">Passwords don't match</span> : null}

            <button className="refresh-btn auth-submit" type="submit" disabled={isDisabled}>
              {submitting ? "Saving..." : "Save new password"}
            </button>
          </form>
        ) : null}
        {error ? <p className="error-banner auth-error">{error}</p> : null}
      </section>
    </main>
  );
}
